import {
  NoAccountsOutlined,
  ProductionQuantityLimitsOutlined,
} from "@mui/icons-material";
import {
  Box,
  Grid,
  Pagination,
  Paper,
  Typography,
  useTheme,
  Fade,
  Grow,
  Zoom,
  styled,
} from "@mui/material";
import PropTypes from "prop-types";
import AttendeeCard from "./AttendeeCard";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[4],
  transition: "all 0.3s ease",
}));

const AnimatedGrid = styled(Grid)(({ theme }) => ({
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
  },
}));

export default function FilteredAttendees({
  refresh,
  filteredAttendees,
  paginatedAttendees,
  itemsPerPage,
  currentPage,
  handlePageChange,
  setFilteredAttendees,
  role,
  user,
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  // console.log("???????????????", user, "?>>>>");
  const handleAttendeeChange = (updatedAttendee) => {
    setFilteredAttendees((prevAttendees) =>
      prevAttendees.map((attendee) =>
        attendee.id === updatedAttendee.id ? updatedAttendee : attendee
      )
    );
  };

  useEffect(() => {}, [filteredAttendees]);

  return (
    <Box sx={{ position: "relative" }}>
      <StyledPaper
        elevation={3}
        style={{ width: "100%", padding: "10px 10px" }}
      >
        {filteredAttendees.length === 0 ? (
          <Fade in timeout={500}>
            <Box
              minHeight="50vh"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              textAlign="center"
            >
              <Zoom in timeout={800}>
                {role === "products" ? (
                  <ProductionQuantityLimitsOutlined
                    sx={{
                      fontSize: 120,
                      color: theme.palette.text.disabled,
                      mb: 2,
                      opacity: 0.8,
                    }}
                  />
                ) : (
                  <NoAccountsOutlined
                    sx={{
                      fontSize: 120,
                      color: theme.palette.text.disabled,
                      mb: 2,
                      opacity: 0.8,
                    }}
                  />
                )}
              </Zoom>
              <Typography
                variant="h5"
                component="div"
                color="textSecondary"
                sx={{
                  fontWeight: 500,
                  maxWidth: 400,
                  lineHeight: 1.4,
                  mb: 2,
                }}
              >
                {t(`no${role}Found`)}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ opacity: 0.8 }}
              >
                {t("tryAdjustingFilters")}
              </Typography>
            </Box>
          </Fade>
        ) : (
          <Grid container spacing={3}>
            {paginatedAttendees?.map((attendee, index) => (
              <AnimatedGrid
                item
                xs={12}
                sm={6}
                md={4}
                lg={4}
                key={attendee.id || index}
              >
                <Grow in timeout={index * 100 + 500}>
                  <div>
                    <AttendeeCard
                      user={user}
                      attendee={attendee}
                      attendees={paginatedAttendees}
                      onAttendeeChange={handleAttendeeChange}
                      refresh={refresh}
                      role={role}
                    />
                  </div>
                </Grow>
              </AnimatedGrid>
            ))}
          </Grid>
        )}
      </StyledPaper>

      {filteredAttendees.length > 0 && (
        <Fade in timeout={800}>
          <Box
            sx={{
              mt: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Pagination
              count={Math.ceil(filteredAttendees.length / itemsPerPage)}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              sx={{
                "& .MuiPaginationItem-root": {
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                },
                "& .Mui-selected": {
                  boxShadow: theme.shadows[2],
                },
              }}
            />
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ opacity: 0.8 }}
            >
              {t("showingResults", {
                from: (currentPage - 1) * itemsPerPage + 1,
                to: Math.min(
                  currentPage * itemsPerPage,
                  filteredAttendees.length
                ),
                total: filteredAttendees.length,
              })}
            </Typography>
          </Box>
        </Fade>
      )}
    </Box>
  );
}

FilteredAttendees.propTypes = {
  filteredAttendees: PropTypes.array.isRequired,
  paginatedAttendees: PropTypes.array.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired,
  role: PropTypes.string.isRequired,
  setFilteredAttendees: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
};
